function getErrorPayload(error: unknown): { error?: string; message?: string; details?: unknown } | null {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof error.response === "object" &&
    error.response !== null &&
    "data" in error.response
  ) {
    return error.response.data as { error?: string; message?: string; details?: unknown }
  }
  return null
}

export function getErrorMessage(error: unknown): string {
  const payload = getErrorPayload(error)
  return payload?.error || payload?.message || "No se pudo procesar la solicitud"
}

/**
 * Extracts a single zod-flatten fieldError (e.g. `video_id`/`document_id` conflicts
 * from PUT /lessons/:id) so the caller can surface it next to the offending field
 * instead of as a generic form-level message.
 */
export function getFieldError(error: unknown, field: string): string | undefined {
  const payload = getErrorPayload(error)
  const details = payload?.details as { fieldErrors?: Record<string, string[]> } | undefined
  return details?.fieldErrors?.[field]?.[0]
}

export function getErrorStatus(error: unknown): number | undefined {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof error.response === "object" &&
    error.response !== null &&
    "status" in error.response &&
    typeof error.response.status === "number"
  ) {
    return error.response.status
  }
  return undefined
}

const MISSING_REQUIREMENT_LABELS: Record<string, string> = {
  title: "título",
  description: "descripción",
  lessons: "lecciones",
  instructor: "instructor",
}

/**
 * PUT /academy/courses/:id responde 400 con `details: { missing: string[] }`
 * cuando se intenta publicar un curso sin los requisitos mínimos. Traduce esa
 * lista a un mensaje legible; para cualquier otro error retorna el mensaje
 * genérico de getErrorMessage.
 */
export function getPublishErrorMessage(error: unknown): string {
  const payload = getErrorPayload(error)
  const missing = (payload?.details as { missing?: string[] } | undefined)?.missing
  if (Array.isArray(missing) && missing.length > 0) {
    const labels = missing.map((key) => MISSING_REQUIREMENT_LABELS[key] ?? key)
    return `Falta: ${labels.join(", ")}`
  }
  return getErrorMessage(error)
}

const FIELD_LABELS: Record<string, string> = {
  name: "nombre",
  slug: "slug",
  region: "región",
  description: "descripción",
  eventStartDate: "inicio del evento",
  eventEndDate: "fin del evento",
  registrationStartDate: "apertura de inscripciones",
  registrationDeadline: "cierre de inscripciones",
  teamPriceCop: "precio por equipo",
  consentText: "texto de consentimiento",
  categorySlug: "categoría",
  seedbedId: "semillero",
}

/**
 * Los endpoints admin de CCCD responden 400 con `details: { fieldErrors, formErrors }`
 * (formato zod flatten()) cuando falla la validación. Traduce esos errores por campo
 * a un mensaje legible en vez del mensaje genérico "Validation failed"; para cualquier
 * otro error retorna el mensaje genérico de getErrorMessage.
 */
export function getValidationErrorMessage(error: unknown): string {
  const payload = getErrorPayload(error)
  const details = payload?.details as { fieldErrors?: Record<string, string[]>; formErrors?: string[] } | undefined
  const fieldMessages = Object.entries(details?.fieldErrors ?? {})
    .filter(([, msgs]) => msgs?.length)
    .map(([field, msgs]) => `${FIELD_LABELS[field] ?? field}: ${msgs[0]}`)
  const messages = [...(details?.formErrors ?? []), ...fieldMessages]
  return messages.length > 0 ? messages.join("; ") : getErrorMessage(error)
}
